import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Plus, Trash2, Edit2, ChevronDown } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { confirmToast } from '../../utils/confirmToast';

// Utility function to truncate text
const truncateText = (text: string | undefined, maxLength: number = 150): string => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, text.lastIndexOf(' ', maxLength)) + '...';
};

// Updated Bucket interface to match MongoDB schema
interface Bucket {
  _id: string;
  name: string;
  image: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// SubBucket interface for separate collection
interface SubBucket {
  _id: string;
  name: string;
  parent_bucket: string;
  image?: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const BucketSection: React.FC = () => {
  const API_BASE_URL = import.meta.env.VITE_API_URL?.replace(/\/$/, "");
  const [buckets, setBuckets] = useState<Bucket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null); // Added success message state
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingBucket, setEditingBucket] = useState<Bucket | null>(null);
  const [formData, setFormData] = useState({ 
    name: '', 
    image: '', 
    subname: '', 
    description: '' 
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null); // Added selectedFile state
  const [subBuckets, setSubBuckets] = useState<SubBucket[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedBucket, setSelectedBucket] = useState<Bucket | null>(null);
  const [isCreatingNew, setIsCreatingNew] = useState(true);
  const [filteredBuckets, setFilteredBuckets] = useState<Bucket[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLDivElement>(null);

  // Fetch buckets from API
  const fetchBuckets = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/buckets`);
      const data = await response.json();
      
      if (data.success) {
        setBuckets(data.buckets);
      } else {
        setError('Failed to fetch buckets');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error('Fetch buckets error:', err);
    } finally {
      setLoading(false);
    }
  };

   // Create new bucket or sub-bucket

  // const createBucket = async (e: React.FormEvent) => {
  //   e.preventDefault();
  //   console.log('Form submitted with data:', formData);
  //   try {
  //     if (isCreatingNew) {
  //       const bucketData = {
  //         name: formData.name,
  //         heading: formData.heading,
  //         image: formData.image,
  //         description: formData.description
  //       };
  //       console.log('Sending bucket data to server:', bucketData);
        
  //       // Create new main bucket
  //       console.log('Sending POST request to /api/buckets with:', bucketData);
  //       const response = await fetch(`${API_BASE_URL}/api/buckets`, {
  //         method: 'POST',
  //         headers: {
  //           'Content-Type': 'application/json',
  //         },
  //         body: JSON.stringify(bucketData),
  //       });

  //       const data = await response.json();
  //       console.log('Server response:', data);
        
  //       if (data.success) {
  //         setBuckets([data.bucket, ...buckets]);
  //         setFormData({ name: '', heading: '', image: '', subname: '', description: '' });
  //         setShowAddForm(false);
  //         clearFormStorage();
  //       } else {
  //         setError(data.message || 'Failed to create bucket');
  //       }
  //     } else {
  //       // Create sub-bucket - require image for sub-bucket
  //       if (!formData.image) {
  //         setError('Please provide an image for the sub-bucket');
  //         return;
  //       }

  //       const subBucketData = {
  //         name: formData.subname,
  //         parent_bucket: selectedBucket?._id, // Store parent bucket ID instead of name
  //         image: formData.image, // Use the provided image, don't inherit
  //         description: formData.description,
  //         heading: formData.heading || '' // Include the heading field
  //       };
        
  //       console.log('Creating sub-bucket with data:', JSON.stringify(subBucketData, null, 2));
  //       console.log('Creating sub-bucket with data:', subBucketData);
        
  //       const response = await fetch(`${API_BASE_URL}/api/subbuckets`, {
  //         method: 'POST',
  //         headers: {
  //           'Content-Type': 'application/json',
  //         },
  //         body: JSON.stringify(subBucketData),
  //       });

  //       const data = await response.json();
  //       console.log('Sub-bucket creation response:', data);
        
  //       if (data.success) {
  //         setSubBuckets([data.subBucket, ...subBuckets]);
  //         setFormData({ name: '', heading: '', image: '', subname: '', description: '' });
  //         setShowAddForm(false);
  //         setSelectedBucket(null);
  //         setIsCreatingNew(true);
  //       } else {
  //         console.error('Sub-bucket creation failed:', data);
  //         setError(data.message || 'Failed to create sub-bucket');
  //       }
  //     }
  //   } catch (err) {
  //     console.error('Create error details:', err);
  //     setError(isCreatingNew ? 'Error creating bucket' : `Error creating sub-bucket: ${err instanceof Error ? err.message : 'Unknown error'}`);
  //   }
  // };

  const createBucket = async (e: React.FormEvent) => {
    e.preventDefault();
    let finalImageUrl = formData.image;

    // Validation: Ensure either a file is selected or a URL is entered
    if (selectedFile && formData.image) {
      setError('Please either upload a file OR paste a URL, not both.');
      return;
    }
    
    if (!selectedFile && !formData.image) {
      setError('You must upload an image or provide an image URL.');
      return;
    }

    try {
      if (isCreatingNew) {
        // 1. If a file is selected, upload it first
        if (selectedFile) {
          const uploadFormData = new FormData();
          uploadFormData.append('image', selectedFile);

          const uploadRes = await fetch(`${API_BASE_URL}/api/upload`, {
            method: 'POST',
            body: uploadFormData,
          });
          const uploadData = await uploadRes.json();

          if (!uploadRes.ok || !uploadData.success) {
            throw new Error(uploadData.message || 'Image upload failed');
          }
          finalImageUrl = uploadData.imageUrl; // Get the URL from the backend
        }

        // Logic for creating a new main bucket
        const bucketData = {
          name: formData.name,
          image: finalImageUrl,
          description: formData.description
        };
        
        const response = await fetch(`${API_BASE_URL}/api/buckets`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(bucketData),
        });
  
        const data = await response.json();
        if (!response.ok) {
          let errorMessage = data.message || 'Failed to create bucket';
          if (data.errors && Array.isArray(data.errors)) {
            errorMessage = `Validation failed: ${data.errors.join(', ')}`;
          }
          throw new Error(errorMessage);
        }
  
        setBuckets([data.bucket, ...buckets]);
        setSuccessMessage('Bucket created successfully!'); // Set success message
        cancelForm(); // Use your cancel function to reset everything
        clearFormStorage();
  
      } else {
        // Logic for creating a new sub-bucket
        if (!selectedBucket?._id) {
          setError('A parent bucket must be selected.');
          return;
        }

        // 1. If a file is selected, upload it first for sub-bucket
        if (selectedFile) {
          const uploadFormData = new FormData();
          uploadFormData.append('image', selectedFile);

          const uploadRes = await fetch(`${API_BASE_URL}/api/upload`, {
            method: 'POST',
            body: uploadFormData,
          });
          const uploadData = await uploadRes.json();

          if (!uploadRes.ok || !uploadData.success) {
            throw new Error(uploadData.message || 'Sub-bucket image upload failed');
          }
          finalImageUrl = uploadData.imageUrl; // Get the URL from the backend
        }
  
        const subBucketData = {
          name: formData.subname,
          parent_bucket: selectedBucket._id,
          image: finalImageUrl,
          description: formData.description
        };
  
        // The URL should be nested under the parent bucket's ID
        const response = await fetch(`${API_BASE_URL}/api/buckets/${selectedBucket._id}/sub-buckets`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(subBucketData),
        });
  
        const data = await response.json();
        if (!response.ok) {
          let errorMessage = data.message || 'Failed to create sub-bucket';
          if (data.errors && Array.isArray(data.errors)) {
            errorMessage = `Sub-bucket validation failed: ${data.errors.join(', ')}`;
          }
          throw new Error(errorMessage);
        }
        
        fetchSubBuckets(); // Re-fetch all sub-buckets to update the UI
        setSuccessMessage('Sub-bucket created successfully!'); // Set success message
        cancelForm(); // Use your cancel function to reset everything
  
      }
    } catch (err: any) {
      // General error handling
      let errorMessage = 'An unknown error occurred.';
      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === 'string') {
        errorMessage = err;
      }
      setError(errorMessage);
      console.error('Create error details:', err);
    }
  };

  // Update bucket
  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingBucket) {
      return;
    }

    let finalImageUrl = formData.image; // Start with the existing image URL

    // 1. Handle new image upload if a file is selected for editing
    if (selectedFile) {
      const uploadFormData = new FormData();
      uploadFormData.append('image', selectedFile);

      try {
        const uploadRes = await fetch(`${API_BASE_URL}/api/upload`, {
          method: 'POST',
          body: uploadFormData,
        });
        const uploadData = await uploadRes.json();

        if (!uploadData.success) {
          throw new Error(uploadData.message || 'Image upload failed');
        }
        finalImageUrl = uploadData.imageUrl; // Get the NEW URL from the backend
      } catch (err: any) {
        setError(err.message);
        return; // Stop if the new image upload fails
      }
    }

    try {
const response = await fetch(`${API_BASE_URL}/api/buckets/${editingBucket._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          image: finalImageUrl, // Use the final image URL
          description: formData.description
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        const updatedBuckets = buckets.map(bucket => 
          bucket._id === editingBucket._id ? data.bucket : bucket
        );
        
        setBuckets(updatedBuckets);
        setSuccessMessage('Bucket updated successfully!'); // Set success message
        setFormData({ name: '', image: '', subname: '', description: '' });
        setEditingBucket(null);
        setShowAddForm(false);
        setSelectedFile(null); // Clear selected file
      } else {
        setError(data.message || 'Failed to update bucket');
      }
    } catch (err) {
      setError('Error updating bucket');
      console.error('Update bucket error:', err);
    }
  };

  // Delete bucket
  const deleteBucket = async (bucketId: string) => {
    const confirmed = await confirmToast('Are you sure you want to delete this bucket?', {
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });
    if (!confirmed) return;

    try {
  const response = await fetch(`${API_BASE_URL}/api/buckets/${bucketId}`, {
        method: 'DELETE',
      });

      const data = await response.json();
      
      if (data.success) {
        setBuckets(buckets.filter(bucket => bucket._id !== bucketId));
        setSuccessMessage('Bucket deleted successfully!'); // Set success message
        toast.success('Bucket deleted');
      } else {
        setError(data.message || 'Failed to delete bucket');
        toast.error(data.message || 'Failed to delete bucket');
      }
    } catch (err) {
      setError('Error deleting bucket');
      toast.error('Error deleting bucket');
      console.error('Delete bucket error:', err);
    }
  };

  // Handle form submission
  const handleSubmit = editingBucket ? handleUpdate : createBucket;

  // Start editing a bucket
  const startEdit = (bucket: Bucket) => {
    setEditingBucket(bucket);
    setFormData({
      name: bucket.name,
      image: bucket.image,
      subname: '',
      description: bucket.description || ''
    });
    setSelectedFile(null); // Clear any previously selected file
    setShowAddForm(true);
  };

  // Cancel form
  const cancelForm = () => {
    setShowAddForm(false);
    setEditingBucket(null);
  setFormData({ name: '', image: '', subname: '', description: '' });
    setSelectedBucket(null);
    setIsCreatingNew(true);
    setShowDropdown(false);
    setFilteredBuckets(buckets);
    setSelectedFile(null); // Clear selected file
    clearFormStorage();
  };

  // Handle bucket selection from dropdown
  const handleBucketSelect = (bucket: Bucket) => {
    setSelectedBucket(bucket);
    setIsCreatingNew(false);
    // Clear form for sub-bucket creation - only keep bucket name for reference
    setFormData({ 
      name: bucket.name, 
      image: '', 
      subname: '', 
      description: '' 
    });
    setShowDropdown(false);
  };

  // Always reset state when opening add form to avoid stale sub-bucket/edit state
  const openAddForm = () => {
    setEditingBucket(null);
    setIsCreatingNew(true);
    setSelectedBucket(null);
    setShowAddForm(true);
    setFormData({ name: '', image: '', subname: '', description: '' });
    setSelectedFile(null);
    setShowDropdown(false);
    setFilteredBuckets(buckets);
  };

  // Handle input focus to show dropdown
  const handleBucketNameFocus = () => {
    if (!editingBucket) {
      setShowDropdown(true);
    }
  };

  // Handle bucket name input change with filtering
  const handleBucketNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData({ ...formData, name: value });
    
    // Filter existing buckets based on input
    const filtered = buckets.filter(bucket => 
      bucket.name.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredBuckets(filtered);
    
    // Check if exact match exists
    const exactMatch = buckets.find(bucket => 
      bucket.name.toLowerCase() === value.toLowerCase()
    );
    
    if (exactMatch) {
      setSelectedBucket(exactMatch);
      setIsCreatingNew(false);
    } else {
      setSelectedBucket(null);
      setIsCreatingNew(true);
    }
    
    // Show dropdown when typing
    if (value.length > 0) {
      setShowDropdown(true);
    }
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  // Initialize filtered buckets
  useEffect(() => {
    setFilteredBuckets(buckets);
  }, [buckets]);

  // Fetch sub-buckets from API
  const fetchSubBuckets = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/subbuckets`);
      const data = await response.json();
      
      if (data.success) {
        setSubBuckets(data.subBuckets);
      } else {
        console.error('Failed to fetch sub-buckets');
      }
    } catch (err) {
      console.error('Fetch sub-buckets error:', err);
    }
  };

  // Load form data from localStorage on component mount
  useEffect(() => {
    const savedFormData = localStorage.getItem('bucketFormData');
    if (savedFormData) {
      setFormData(JSON.parse(savedFormData));
    }
  }, []);

  // Save form data to localStorage whenever it changes
  useEffect(() => {
    if (Object.values(formData).some(value => value !== '')) {
      localStorage.setItem('bucketFormData', JSON.stringify(formData));
    }
  }, [formData]);

  // Clear form data from localStorage when form is submitted or cancelled
  const clearFormStorage = () => {
    localStorage.removeItem('bucketFormData');
  };

  // Effect to clear success message after a few seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 3000); // Clear after 3 seconds
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Effect to clear error message after a few seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 3000); // Clear after 3 seconds
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Load buckets and sub-buckets on component mount
  useEffect(() => {
    fetchBuckets();
    fetchSubBuckets();
  }, []);

  // Scroll form into view when adding or editing
  useEffect(() => {
    if (showAddForm && formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showAddForm]);

  useEffect(() => {
    if (editingBucket && formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [editingBucket]);

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
      >
        <div>
          <h1 className="font-poppins text-3xl font-bold text-gray-900">Buckets</h1>
          <p className="font-inter text-gray-600 mt-1">Organize your content into categories</p>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={openAddForm}
          className="bg-primary text-white px-4 py-2 rounded-lg flex items-center justify-center space-x-2 hover:bg-primary/90 transition-colors w-full sm:w-auto"
        >
          <Plus className="w-4 h-4" />
          <span className="font-inter font-medium">Add Bucket</span>
        </motion.button>
      </motion.div>

      {/* Error Message */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded"
        >
          {error}
          <button
            onClick={() => setError(null)}
            className="float-right font-bold text-red-700 hover:text-red-900"
          >
            ×
          </button>
        </motion.div>
      )}

      {/* Success Message */}
      {successMessage && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded"
        >
          {successMessage}
          <button
            onClick={() => setSuccessMessage(null)}
            className="float-right font-bold text-green-700 hover:text-green-900"
          >
            ×
          </button>
        </motion.div>
      )}

      {/* Add/Edit Form */}
      {showAddForm && (
        <motion.div
          ref={formRef}
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="bg-white rounded-xl p-6 shadow-lg border border-gray-100"
        >
          <div className="mb-4">
            <h3 className="font-poppins text-lg font-semibold">
              {editingBucket 
                ? 'Edit Bucket' 
                : isCreatingNew 
                  ? 'Add New Bucket' 
                  : `Add New Sub-bucket to ${selectedBucket?.name}`}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {editingBucket 
                ? 'Update the bucket details below' 
                : isCreatingNew 
                  ? 'Fill in the details to create a new bucket' 
                  : 'Fill in the details to add a new sub-bucket'}
            </p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="relative" ref={dropdownRef}>
              <label className="block font-inter text-sm font-medium text-gray-700 mb-2">
                Bucket Name <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.name}
                  onChange={handleBucketNameChange}
                  onFocus={handleBucketNameFocus}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder={!editingBucket ? "Type to search existing buckets or create new" : "Enter bucket name"}
                  required
                />
                {!editingBucket && (
                  <ChevronDown 
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" 
                  />
                )}
              </div>
              
              {/* Dropdown */}
              {showDropdown && !editingBucket && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredBuckets.length > 0 ? (
                    <>
                      {/* Show filtered existing buckets */}
                      {filteredBuckets.map((bucket) => (
                        <div
                          key={bucket._id}
                          onClick={() => handleBucketSelect(bucket)}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center space-x-2"
                        >
                          <img 
                            src={bucket.image} 
                            alt={bucket.name} 
                            className="w-6 h-6 rounded object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = '/api/placeholder/24/24';
                            }}
                          />
                          <span>{bucket.name}</span>
                        </div>
                      ))}
                      {/* Show create new option if no exact match */}
                      {isCreatingNew && formData.name && (
                        <div className="px-3 py-2 text-gray-500 border-t border-gray-200 font-medium">
                          + Create new bucket: "{formData.name}"
                        </div>
                      )}
                    </>
                  ) : (
                    /* Show create new when no matches */
                    formData.name && (
                      <div className="px-3 py-2 text-gray-500 font-medium">
                        + Create new bucket: "{formData.name}"
                      </div>
                    )
                  )}
                </div>
              )}
            </div>
            {/* Only show sub-bucket name field when creating a new sub-bucket */}
            {!isCreatingNew && !editingBucket && (
              <div>
                <label className="block font-inter text-sm font-medium text-gray-700 mb-2">
                  Sub Bucket Name <span className="text-xs text-gray-500">(Adding to: {selectedBucket?.name})</span>
                </label>
                <input
                  type="text"
                  value={formData.subname}
                  onChange={(e) => setFormData({ ...formData, subname: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Enter sub bucket name"
                  required
                />
              </div>
            )}

            <div className="md:col-span-2">
              <label className="block font-inter text-sm font-medium text-gray-700 mb-2">
                {isCreatingNew || editingBucket ? 'Bucket Image' : 'Sub-bucket Image'}
                {!isCreatingNew && !editingBucket && (
                  <span className="text-xs text-red-500 ml-1">* Required</span>
                )}
              </label>
              <div className="flex items-center space-x-2">
                <input 
                  type="url" 
                  placeholder="Paste Image URL"
                  value={formData.image} 
                  onChange={(e) => {
                    setFormData({ ...formData, image: e.target.value });
                    setSelectedFile(null); // Clear selected file if URL is entered
                  }} 
                  className="flex-grow px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" 
                  required={!selectedFile} // Required if no file is selected
                />
                <span className="text-gray-500">OR</span>
                <label className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors font-inter cursor-pointer">
                  Upload
                  <input 
                    type="file" 
                    onChange={(e) => {
                      setSelectedFile(e.target.files ? e.target.files[0] : null);
                      setFormData({ ...formData, image: '' }); // Clear URL if file is selected
                    }} 
                    className="hidden" 
                    accept="image/*" 
                    required={!formData.image} // Required if no URL is entered
                  />
                </label>
              </div>
              {selectedFile && <p className="text-sm text-gray-600 mt-2">File selected: {selectedFile.name}</p>}
              {!isCreatingNew && !editingBucket && (
                <p className="mt-1 text-xs text-gray-500">
                  Sub-buckets must have their own image and do not inherit from the parent bucket.
                </p>
              )}
            </div>
            
            <div>
              <label className="block font-inter text-sm font-medium text-gray-700 mb-2">
                Description (Optional)
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                rows={3}
              />
            </div>
            <div className="flex space-x-3">
              <button
                type="submit"
                className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors font-inter"
              >
                {editingBucket ? 'Update Bucket' : (isCreatingNew ? 'Create Bucket' : 'Add Sub Bucket')}
              </button>
              <button
                type="button"
                onClick={cancelForm}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors font-inter"
              >
                Cancel
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      )}

      {/* Buckets Grid */}
      {!loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {buckets.map((bucket, index) => (
            <motion.div
              key={bucket._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              className="flex flex-col items-center group relative"
            >
              {/* Action Buttons */}
              <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                <div className="flex space-x-1">
                  <button
                    onClick={() => startEdit(bucket)}
                    className="bg-blue-500 text-white p-1 rounded hover:bg-blue-600 transition-colors"
                    title="Edit bucket"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => deleteBucket(bucket._id)}
                    className="bg-red-500 text-white p-1 rounded hover:bg-red-600 transition-colors"
                    title="Delete bucket"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Bucket Image */}
              <div className="w-full aspect-square">
                <img
                  src={bucket.image}
                  alt={bucket.name}
                  className="w-full h-full object-cover rounded-lg"
                />
              </div>
              
              {/* Bucket Info */}
              <div className="text-center mt-4 px-2">
                <h2 className="font-inter font-medium text-xl md:text-2xl">
                  {bucket.name}
                </h2>
                {bucket.description && (
                  <p className="font-inter text-sm text-gray-600 mt-2 line-clamp-3">
                    {truncateText(bucket.description, 150)}
                  </p>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && buckets.length === 0 && (
        <div className="text-center py-12">
          <p className="font-inter text-gray-500 text-lg">No buckets found. Create your first bucket!</p>
        </div>
      )}
    </div>
  );
};

export default BucketSection;
